import { useLocalSearchParams } from 'expo-router'
import type React from 'react'
import { useCallback, useState } from 'react'
import { Platform, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { captureException } from '@/lib/errors'
import { pb } from '@/lib/pocketbase'
import { Button } from '@/ui/Button'
import { type Control, TextInput, useForm, z, zodResolver } from '@/ui/form'
import { ButtonLink, Link } from '@/ui/Link'

const resetPasswordConfirmSchema = z
    .object({
        password: z
            .string()
            .min(8, 'Password must be at least 8 characters')
            .min(1, 'Password is required'),
        confirmPassword: z.string().min(1, 'Please confirm your password'),
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    })

type ResetPasswordConfirmFormData = z.infer<typeof resetPasswordConfirmSchema>

const useResetPasswordConfirmSubmission = (token: string | null) => {
    const [isLoading, setIsLoading] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isSuccess, setIsSuccess] = useState(false)

    const onSubmit = useCallback(
        async (data: ResetPasswordConfirmFormData) => {
            if (!token) return

            setIsLoading(true)
            setSubmitError(null)

            try {
                await pb
                    .collection('users')
                    .confirmPasswordReset(token, data.password, data.confirmPassword)

                setIsSuccess(true)
            } catch (error) {
                captureException('Password reset confirmation', error)
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : 'Failed to reset password. Please try again.'
                setSubmitError(errorMessage)
            } finally {
                setIsLoading(false)
            }
        },
        [token]
    )

    return { onSubmit, isLoading, submitError, isSuccess }
}

interface ResetPasswordConfirmFormProps {
    control: Control<ResetPasswordConfirmFormData>
    onSubmit: () => void
    isLoading: boolean
    isValid: boolean
    submitError: string | null
}

const ResetPasswordConfirmForm: React.FC<ResetPasswordConfirmFormProps> = ({
    control,
    onSubmit,
    isLoading,
    isValid,
    submitError,
}) => {
    const semanticColors = useSemanticColors()

    return (
        <View style={{ gap: 16 }}>
            <TextInput
                name="password"
                control={control}
                label="New Password"
                placeholder="Enter your new password"
                secureTextEntry
                autoComplete="new-password"
            />

            <TextInput
                name="confirmPassword"
                control={control}
                label="Confirm New Password"
                placeholder="Confirm your new password"
                secureTextEntry
                autoComplete="new-password"
            />

            {submitError && (
                <Text style={{ fontSize: 14, color: semanticColors.errorText }}>{submitError}</Text>
            )}

            <Button
                title="Reset Password"
                mode="contained"
                onPress={onSubmit}
                disabled={isLoading || !isValid}
                loading={isLoading}
                loadingTitle="Resetting Password..."
            />
        </View>
    )
}

export default function ResetPasswordConfirmPage() {
    useAvoidSoftInputForScreen()
    const { token } = useLocalSearchParams<{ token?: string }>()
    const theme = useTheme()
    const semanticColors = useSemanticColors()

    const ContentWrapper = Platform.OS === 'ios' ? SafeAreaView : View

    const tokenValue = typeof token === 'string' ? token : null
    const { onSubmit, isLoading, submitError, isSuccess } =
        useResetPasswordConfirmSubmission(tokenValue)

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<ResetPasswordConfirmFormData>({
        resolver: zodResolver(resetPasswordConfirmSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    })

    if (!tokenValue) {
        return (
            <ContentWrapper style={{ flex: 1, backgroundColor: theme.colors.background }}>
                <View
                    style={{
                        flex: 1,
                        padding: 16,
                        gap: 16,
                        maxWidth: 400,
                        width: '100%',
                        alignSelf: 'center',
                    }}
                >
                    <View style={{ gap: 8, marginBottom: 16 }}>
                        <Text style={{ fontSize: 24, fontWeight: 'bold' }}>Invalid Reset Link</Text>
                        <Text style={{ color: semanticColors.errorText }}>
                            This password reset link is invalid or has expired.
                        </Text>
                    </View>

                    <ButtonLink href="/auth/reset" title="Request New Reset Link" />
                </View>
            </ContentWrapper>
        )
    }

    if (isSuccess) {
        return (
            <ContentWrapper style={{ flex: 1, backgroundColor: theme.colors.background }}>
                <View
                    style={{
                        flex: 1,
                        padding: 16,
                        gap: 16,
                        maxWidth: 400,
                        width: '100%',
                        alignSelf: 'center',
                    }}
                >
                    <View style={{ gap: 8, marginBottom: 16 }}>
                        <Text style={{ fontSize: 24, fontWeight: 'bold' }}>
                            Password Reset Successful
                        </Text>
                        <Text style={{ color: semanticColors.successText }}>
                            Your password has been successfully reset. You can now sign in with your
                            new password.
                        </Text>
                    </View>

                    <ButtonLink href="/auth/sign-in" title="Sign In" />
                </View>
            </ContentWrapper>
        )
    }

    return (
        <ContentWrapper style={{ flex: 1, backgroundColor: theme.colors.background }}>
            <View
                style={{
                    flex: 1,
                    padding: 16,
                    gap: 16,
                    maxWidth: 400,
                    width: '100%',
                    alignSelf: 'center',
                }}
            >
                <View style={{ gap: 8, marginBottom: 16 }}>
                    <Text style={{ fontSize: 24, fontWeight: 'bold' }}>Set New Password</Text>
                    <Text style={{ color: theme.colors.onSurfaceVariant }}>
                        Enter your new password below
                    </Text>
                </View>

                <ResetPasswordConfirmForm
                    control={control}
                    onSubmit={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                    isValid={isValid}
                    submitError={submitError}
                />

                <View style={{ gap: 12, marginTop: 16 }}>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                        <Text style={{ color: theme.colors.onSurfaceVariant }}>
                            Remember your password?
                        </Text>
                        <Link href="/auth/sign-in">
                            <Text style={{ color: theme.colors.primary }}>Sign In</Text>
                        </Link>
                    </View>
                </View>
            </View>
        </ContentWrapper>
    )
}
